import { Box, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { CardDTO, ChallengeAction, ChallengeStep, PlayerInfo, TeamId, toCardCode } from "../interface/GameTypes";

type Props = {
  open: boolean;
  step: ChallengeStep;
  stake: number;
  trumpSuit?: string | null;
  myHand: CardDTO[];
  callerLabel: string;
  onChoose: (action: ChallengeAction) => void;
  mySeatIndex: number;
  passedSeatIndices: number[];
  players: PlayerInfo[];
  respondingTeam: TeamId;
};

const STEP_META: Record<
  ChallengeStep,
  {
    title: string;
    escalateLabel: string;
    escalate: Exclude<ChallengeAction, ChallengeAction.Pass>;
    body: string;
  }
> = {
  [ChallengeStep.Addu]: {
    title: "Addu?",
    escalateLabel: "Addu",
    escalate: ChallengeAction.Addu,
    body: "Trump is set. The other team has Atta. Your team may call Addu to double the stake, or everyone Passes to continue without raising.",
  },
  [ChallengeStep.Sharathu]: {
    title: "Sharathu?",
    escalateLabel: "Sharathu",
    escalate: ChallengeAction.Sharathu,
    body: "The opposition called Addu. Your team may call Sharathu to raise the stake again, or everyone Passes.",
  },
  [ChallengeStep.BeSharathu]: {
    title: "Be Sharathu?",
    escalateLabel: "Be Sharathu",
    escalate: ChallengeAction.BeSharathu,
    body: "The other team called Sharathu. Your team may call Be Sharathu for one more raise, or everyone Passes.",
  },
};

const SUIT_SYMBOLS: Record<string, string> = {
  Spades: "♠",
  Hearts: "♥",
  Diamonds: "♦",
  Clubs: "♣",
};

export const ChallengeChainModal = ({
  open,
  step,
  stake,
  trumpSuit,
  myHand,
  callerLabel,
  onChoose,
  mySeatIndex,
  passedSeatIndices,
  players,
  respondingTeam,
}: Props) => {
  const meta = STEP_META[step];
  const trumpLabel = trumpSuit
    ? `${SUIT_SYMBOLS[trumpSuit] ?? trumpSuit} ${trumpSuit}`
    : null;

  const { myHasPassed, waitingLabel } = useMemo(() => {
    const passed = new Set(passedSeatIndices);
    const teamSeats = players.filter((p) => p.teamId === respondingTeam).map((p) => p.seatIndex);
    const waitingSeats = teamSeats.filter((s) => !passed.has(s));
    const waitingNames = waitingSeats.map(
      (si) => players.find((p) => p.seatIndex === si)?.name ?? `Seat ${si}`
    );
    return {
      myHasPassed: passed.has(mySeatIndex),
      waitingLabel: waitingNames.length > 0 ? waitingNames.join(", ") : "—",
    };
  }, [passedSeatIndices, players, respondingTeam, mySeatIndex]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { style: { backdropFilter: "blur(6px)" } },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: "2px solid rgba(255, 193, 7, 0.65)",
          background: "linear-gradient(160deg, rgba(28,40,28,0.98) 0%, rgba(12,18,12,0.99) 100%)",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 24px 48px rgba(0,0,0,0.55)",
          animation: "challengeFlash 2.2s ease-in-out infinite",
          "@keyframes challengeFlash": {
            "0%, 100%": { boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 24px 48px rgba(0,0,0,0.55)" },
            "50%": { boxShadow: "0 0 0 1px rgba(255,193,7,0.35), 0 28px 56px rgba(255,193,7,0.12)" },
          },
        },
      }}
    >
      <DialogTitle sx={{ pt: 2.5, pb: 0 }}>
        <Typography component="span" sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#fff", letterSpacing: 0.3 }}>
          {meta.title}
        </Typography>
        <Typography sx={{ mt: 0.75, fontSize: "0.88rem", color: "rgba(255,255,255,0.55)", fontWeight: 500 }}>
          Atta caller: <Box component="span" sx={{ color: "#81c784", fontWeight: 700 }}>{callerLabel}</Box>
          {" · "}
          Trump: <Box component="span" sx={{ color: "#90caf9", fontWeight: 700 }}>{trumpLabel ?? "—"}</Box>
          {" · "}
          Stake: <Box component="span" sx={{ color: "#ffc107", fontWeight: 700 }}>{stake}</Box>
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 2.5 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.95rem", lineHeight: 1.55, mb: 1.5 }}>
          {meta.body}
        </Typography>
        <Box
          sx={{
            mb: 1.6,
            p: 1,
            borderRadius: 1.25,
            border: "1px solid rgba(255,255,255,0.16)",
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.78rem", fontWeight: 700, mb: 0.75 }}>
            Your cards
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
            {myHand.map((card, i) => {
              const { value, suit } = toCardCode(card);
              const suitSym = suit === "H" ? "♥" : suit === "D" ? "♦" : suit === "S" ? "♠" : "♣";
              const red = suit === "H" || suit === "D";
              return (
                <Box
                  key={`${card.suit}-${card.rank}-${i}`}
                  sx={{
                    px: 0.8,
                    py: 0.35,
                    borderRadius: 0.8,
                    border: "1px solid rgba(255,255,255,0.22)",
                    backgroundColor: "rgba(0,0,0,0.35)",
                    color: red ? "#ef9a9a" : "rgba(255,255,255,0.95)",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    fontFamily: "monospace",
                    lineHeight: 1,
                  }}
                >
                  {value}
                  {suitSym}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", lineHeight: 1.45, mb: 2 }}>
          Still need a choice from:{" "}
          <Box component="span" sx={{ color: "rgba(255,255,255,0.88)", fontWeight: 600 }}>
            {waitingLabel}
          </Box>
        </Typography>
        {myHasPassed ? (
          <Typography
            sx={{
              textAlign: "center",
              py: 1.5,
              px: 1,
              borderRadius: 1,
              bgcolor: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.75)",
              fontSize: "0.95rem",
              fontWeight: 600,
            }}
          >
            You passed. Wait for teammates — anyone on your team can still call {meta.escalateLabel}.
          </Typography>
        ) : (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="stretch">
            <Button
              variant="contained"
              size="large"
              onClick={() => onChoose(meta.escalate)}
              sx={{
                flex: 1,
                py: 1.35,
                fontWeight: 800,
                fontSize: "1rem",
                background: "linear-gradient(180deg, #ffb300 0%, #f57c00 100%)",
                color: "#1a1a1a",
                "&:hover": { background: "linear-gradient(180deg, #ffc107 0%, #ef6c00 100%)" },
              }}
            >
              {meta.escalateLabel}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => onChoose(ChallengeAction.Pass)}
              sx={{
                flex: 1,
                py: 1.35,
                fontWeight: 700,
                borderColor: "rgba(255,255,255,0.35)",
                color: "rgba(255,255,255,0.9)",
                "&:hover": { borderColor: "rgba(255,255,255,0.55)", backgroundColor: "rgba(255,255,255,0.06)" },
              }}
            >
              Pass
            </Button>
          </Stack>
        )}
        <Typography sx={{ mt: 2, fontSize: "0.75rem", color: "rgba(255,255,255,0.38)", textAlign: "center" }}>
          Everyone on your team must Pass to decline. One call ({meta.escalateLabel}) raises the stake for the whole team.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};
